import { ContactContactType, CreateContactInput, useCreateContactMutation } from "generated/graphql";
import { useContext } from "react";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import MaskedInput from "react-text-mask";
import { BaseInput } from "components/Common/Field/BaseInput";
import { useHoldingInfo } from "@breedr/utils";
import { CountryISOCode } from "@breedr/utils/dist/useHoldingInfo";
import { CommonContext } from "config/commonProvider";
import { useGetCurrentBusinessUnit } from "hooks";
import { Button, Field, FieldText, Flex, Spacer } from "components";
import { yupResolver } from "@hookform/resolvers/yup";
import styles from "./CreateNewContactForm.module.scss";
import { getBUFromLocalStorage } from "helpers/storage";

interface FormValues {
  contactType: string;
  farmId: string | undefined;
  organisationName: string | undefined;
  name: string;
  addressLine1: string | undefined;
  addressLine2: string | undefined;
  postcode: string | undefined;
  email: string | undefined;
  city: string | undefined;
  phoneNumber: string | undefined;
}

const defaultFormValues: FormValues = {
  contactType: ContactContactType.Farm,
  name: "",
  farmId: "",
  organisationName: "",
  addressLine1: "",
  addressLine2: "",
  postcode: "",
  phoneNumber: "",
  email: "",
  city: "",
};

interface Props {
  onConfirm?: () => void;
  onCancel?: () => void;
}

export const CreateNewContactForm = ({ onConfirm, onCancel }: Props) => {
  const currentBusinessUnitId: number = getBUFromLocalStorage();
  const { regulator, countryIsoCode } = useGetCurrentBusinessUnit();
  const { showNotification } = useContext(CommonContext);
  const [createContact, { loading }] = useCreateContactMutation();
  const { HoldingIDInitialism, HoldingIDNumberMaskWeb } = useHoldingInfo(
    countryIsoCode.toUpperCase() as CountryISOCode,
  );
  const radioButtonClasses = { root: styles.root, checked: styles.checked };
  const isScotMoves = regulator?.slug === "SCOTMOVES";

  const onSubmit = async (formValues: FormValues) => {
    try {
      const input: CreateContactInput = {
        businessUnit: currentBusinessUnitId,
        contactType: formValues.contactType,
        name: formValues.name,
        farmId: formValues.farmId || undefined,
        organisationName: formValues.organisationName || undefined,
        addressLine1: formValues.addressLine1,
        addressLine2: formValues.addressLine2 || undefined,
        postcode: formValues.postcode || undefined,
        city: formValues.city || undefined,
        country: undefined,
        phoneNumber: formValues.phoneNumber || undefined,
        email: formValues.email || undefined,
      };

      const result = await createContact({ variables: { input } });

      if (result.data?.createContact?.errors) {
        showNotification({
          variant: "error",
          message: "Error creating new contact.",
        });
      } else {
        showNotification({
          message: "Successfully created new contact",
        });
        if (onConfirm) {
          onConfirm();
        }
      }
    } catch (error: unknown) {}
  };

  const validationSchema = yup.object().shape(
    {
      contactType: yup.string(),
      name: yup.string().required("Name is required").max(40, "Max 40 characters").trim().label("name"),
      farmId: yup.string().when("addressLine1", {
        is: (addressLine1: string) => !addressLine1 ?? addressLine1.length === 0,
        then: yup
          .string()
          .matches(/[0-9]{2}\/[0-9]{3}\/[0-9]{4}/, "Must be format 11/111/1111")
          .required("Address or CPH is required"),
        otherwise: yup.string(),
      }),
      addressLine1: yup.string().when(["farmId", "contactType"], {
        is: (farmId: string, contactType: string) => {
          if (contactType === ContactContactType.Farm) {
            return !farmId ?? farmId.length === 0;
          } else {
            return true;
          }
        },
        //@ts-expect-error
        then: (_: string, contactType: string) =>
          yup
            .string()
            .required(contactType === ContactContactType.Farm ? "Address or CPH is required" : "Address is required"),
        otherwise: yup.string(),
      }),
      email: yup.string().email(),
    },
    [["farmId", "addressLine1"]],
  );

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
    watch,
  } = useForm({
    defaultValues: defaultFormValues,
    resolver: yupResolver(validationSchema),
  });

  const watchContactType = watch("contactType");

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="contactType"
        render={({ field: { value, onChange }, formState: { errors } }) => {
          return (
            <>
              <Field error={errors.contactType?.message} labelFor="contactType" label="Contact Type">
                <RadioGroup aria-label="Contact type" name="contactType" value={value} onChange={onChange} row>
                  <FormControlLabel
                    value={ContactContactType.Farm}
                    control={<Radio classes={radioButtonClasses} />}
                    label="Farm"
                  />
                  {!isScotMoves && (
                    <FormControlLabel
                      value={ContactContactType.Other}
                      control={<Radio classes={radioButtonClasses} />}
                      label="Other"
                    />
                  )}
                </RadioGroup>
              </Field>
            </>
          );
        }}
      />
      <Controller
        control={control}
        name="name"
        render={({ field, formState: { errors } }) => {
          return (
            <FieldText
              label="Destination Name"
              error={errors.name?.message}
              inputProps={{
                name: "name",
                placeholder: "Enter name",
                value: field.value,
                onChange: field.onChange,
              }}
            />
          );
        }}
      />
      {watchContactType === ContactContactType.Farm && (
        <Controller
          control={control}
          name="farmId"
          render={({ field }) => {
            return (
              <MaskedInput
                label={`Destination ${HoldingIDInitialism} number`}
                mask={HoldingIDNumberMaskWeb}
                name="farmId"
                value={field.value}
                onChange={field.onChange}
                render={(ref, props): JSX.Element => (
                  <Field error={errors.farmId?.message} labelFor="farmId" {...props}>
                    <BaseInput hasError={!!errors.farmId} innerRef={ref} {...props} />
                  </Field>
                )}
              />
            );
          }}
        />
      )}

      <Controller
        control={control}
        name="organisationName"
        render={({ formState: { errors }, field: { value, onChange } }) => {
          return (
            <FieldText
              label="Organisation/Holding Name"
              error={errors.organisationName?.message}
              inputProps={{
                name: "organisationName",
                placeholder: "Holding Name",
                value: value || "",
                onChange,
              }}
            />
          );
        }}
      />

      <label>
        Address
        <Controller
          control={control}
          name="addressLine1"
          render={({ formState: { errors }, field: { value, onChange } }) => {
            return (
              <FieldText
                error={errors.addressLine1?.message}
                inputProps={{
                  name: "addressLine1",
                  placeholder: "Address Line 1",
                  value: value || "",
                  onChange,
                }}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="addressLine2"
          render={({ formState: { errors }, field: { value, onChange } }) => {
            return (
              <FieldText
                error={errors.addressLine2?.message}
                inputProps={{
                  name: "addressLine2",
                  placeholder: "Address Line 2",
                  value: value || "",
                  onChange,
                }}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="postcode"
          render={({ formState: { errors }, field: { value, onChange } }) => {
            return (
              <FieldText
                error={errors.postcode?.message}
                inputProps={{
                  name: "postcode",
                  placeholder: "Postcode",
                  value: value || "",
                  onChange,
                }}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="city"
          render={({ formState: { errors }, field: { value, onChange } }) => {
            return (
              <FieldText
                error={errors.city?.message}
                inputProps={{
                  name: "city",
                  placeholder: "Town/City",
                  value: value || "",
                  onChange,
                }}
              />
            );
          }}
        />
      </label>
      <Controller
        control={control}
        name="phoneNumber"
        render={({ formState: { errors }, field: { value, onChange } }) => {
          return (
            <FieldText
              label="Phone"
              error={errors.phoneNumber?.message}
              inputProps={{
                name: "phoneNumber",
                placeholder: "Phone Number",
                value: value || "",
                onChange,
              }}
            />
          );
        }}
      />
      <Controller
        control={control}
        name="email"
        render={({ formState: { errors }, field: { value, onChange } }) => {
          return (
            <FieldText
              label="Email"
              error={errors.phoneNumber?.message}
              inputProps={{
                name: "email",
                placeholder: "Email Address",
                value: value || "",
                onChange,
              }}
            />
          );
        }}
      />

      <Spacer baselineHeight={1} />
      <Flex container containerJustifyContent="flex-end">
        <Flex item itemGutter>
          <Button caption="Cancel" type="button" colour="grey" onClick={onCancel} disabled={loading || isSubmitting} />
        </Flex>
        <Button
          caption="Confirm"
          type="submit"
          disabled={loading || isSubmitting}
          requesting={loading || isSubmitting}
        />
      </Flex>
    </form>
  );
};
